import { FormControl, FormHelperText, SxProps, Theme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import clsx from "clsx";
import {
  NUMBER_OF_OPS_FIELDS,
  OPERATION_INDEXES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  TRACK_EVENTS,
} from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getCharacterCounter } from "core/model/utils/strings";
import {
  GKVMedicalFormData,
  PredicamentState,
  QueryProgressActive,
} from "core/types";
import { format, fromUnixTime } from "date-fns";
import DatePickerInputField, {
  ConnectedDateSelectorProps,
} from "ds_legacy/components/DatePickerInputField";
import { SubheadingFormLegend } from "ds_legacy/components/FormComponents/SubheadingFormLegend";
import RadioGroupV2, { RadioOptionV2 } from "ds_legacy/components/RadioGroupV2";
import RSButton from "ds_legacy/components/RSButton";
import ConnectedSelectInput, {
  ConnectedSelectInputProps,
} from "ds_legacy/components/SelectInput";
import {
  ConnectedTextarea,
  TextAreaProps,
} from "ds_legacy/components/TextArea";
import ConnectedTextInputField, {
  ConnectedTextInputProps,
  TextInputField,
} from "ds_legacy/components/TextInputField";
import ToggleInputField from "ds_legacy/components/ToggleInputField";
import {
  CUSTOM_BLACK,
  CUSTOM_WHITE,
  GREY_600,
  PRIMARY_DARK_COLOR,
  RADIO_WHITE_BACKGROUND,
  WHITE,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import {
  border,
  dp,
  important,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import {
  Body,
  Caption,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_18,
  FONT_SIZE_24,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_REGULAR,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useVisibleCount } from "dsl/hooks";
import { FolderInputIcon, LockIcon, PlusIcon, PrinterIcon } from "lucide-react";
import { CSSProperties, ReactNode, useMemo } from "react";
import {
  FormElementProps,
  FormElementRenderProps,
  FormWatcher,
  MutateElementFn,
  isValid,
} from "react-forms-state";
import styled from "styled-components";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { parsePerItemValidation } from "./utils";

export type Footnote = {
  description: (translations: Translations) => string;
  label: (translations: Translations) => string;
  superText: 1 | 2;
};

export const GAP = 3;

export const LARGE_INPUT_WIDTH = "35%";
export const LARGE_INPUT_MIN_WIDTH = dp(280);

export const SMALL_INPUT_WIDTH = `calc(17.5% - ${sizing(1.5)})`;
export const SMALL_INPUT_MIN_WIDTH = dp(130);

export const TEXT_AREA_MAX_CHARACTERS = 400;
export const TEXT_AREA_MAX_CHARACTERS_DESCRIPTION = 780;

const DEFAULT_VISIBLE_OPERATION_ROWS = 1;

export const patientSubheadingSx: CSSProperties = {
  color: important(CUSTOM_BLACK),
  fontSize: FONT_SIZE_18,
  fontWeight: FONT_WEIGHT_BOLD,
  margin: margin(0, 0, 0, 0.5),
  padding: padding(GAP, 0, 1),
};

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${sizing(1)};
  background-color: ${CUSTOM_WHITE};
  padding: ${padding(GAP)};
  box-sizing: border-box;
  overflow: visible;
`;

export const SectionRow = styled(HorizontalLayout)<{
  customPadding?: CSSProperties["padding"];
  fullWidth?: boolean;
}>`
  align-items: baseline;
  box-sizing: border-box;
  column-gap: ${sizing(GAP)};
  flex-wrap: wrap;
  overflow: visible;
  padding: ${({ customPadding }) => customPadding ?? padding(GAP, 0, 0)};
`;

const formatDate = (timestamp: number, locale: Locale) =>
  format(fromUnixTime(timestamp), "P", { locale });

const formatDateTime = (timestamp: number, time: number, locale: Locale) =>
  `${formatDate(timestamp, locale)} ${format(fromUnixTime(time), "HH:mm", {
    locale,
  })}`;

export function getDefaultVisibleOperationRows(
  formData: Partial<GKVMedicalFormData> | undefined,
) {
  if (!formData) return DEFAULT_VISIBLE_OPERATION_ROWS;

  let maxFilledIndex = 0;

  Object.values(OPERATION_INDEXES).forEach((opsIndex, i) => {
    const date =
      formData[`operation_${opsIndex}_date` as keyof GKVMedicalFormData];
    const ops =
      formData[`operation_${opsIndex}_ops` as keyof GKVMedicalFormData];
    const woundClosed =
      formData[
        `operation_${opsIndex}_wound_closed` as keyof GKVMedicalFormData
      ];

    if (date || ops || woundClosed) {
      // Record the **index+1** to count all rows up to this one
      maxFilledIndex = i + 1;
    }
  });

  return Math.max(maxFilledIndex, DEFAULT_VISIBLE_OPERATION_ROWS);
}

export const getFootnoteAriaLabel = (
  footNote: Footnote,
  translations: Translations,
) => `${footNote.label(translations)} - ${footNote.description(translations)}`;

export const Section = ({
  children,
  title,
}: {
  children: ReactNode[] | ReactNode;
  title?: string;
}) => {
  return (
    <>
      {title ? (
        <PatientFormsSectionHeader>{title}</PatientFormsSectionHeader>
      ) : null}
      <SectionWrapper>{children}</SectionWrapper>
    </>
  );
};

export const FormSectionsWrapper = ({
  children,
  hideBottomMargin,
}: {
  children: ReactNode[] | ReactNode;
  hideBottomMargin?: boolean;
}) => {
  return (
    <VerticalLayout
      margin={margin(1, 0, hideBottomMargin ? 1 : 3)}
      rowGap={sizing(GAP)}
      width="100%"
    >
      {children}
    </VerticalLayout>
  );
};

/**
 * @deprecated Accessibility issues:
 *   1. label nested inside fieldset and legend is not allowed
 *   2. label missing association with input
 */
export const WithLabel = ({
  children,
  formLabelSx,
  hasError,
  isBold,
  isRequired,
  label,
  wrapperSx,
}: {
  children: ReactNode;
  formLabelSx?: SxProps<Theme>;
  hasError: boolean;
  isBold?: boolean;
  isRequired?: boolean;
  label: ReactNode | undefined;
  wrapperSx: SxProps<Theme>;
}) => {
  return (
    <FormControl component="fieldset" error={hasError} sx={wrapperSx}>
      <SubheadingFormLegend
        sx={{
          fontSize: FONT_SIZE_14,
          fontWeight: isBold ? FONT_WEIGHT_BOLD : undefined,
          margin: margin(0, 0, 1),
          ...formLabelSx,
        }}
      >
        <label>
          {label}
          {isRequired ? " *" : ""}
        </label>
      </SubheadingFormLegend>
      {children}
    </FormControl>
  );
};

const LabelWrapper = ({
  children,
  elementName,
  formLabelSx,
  hasErrorOverride,
  isBold,
  isRequired,
  label,
  wrapperSx,
}: {
  children: ReactNode;
  elementName: string;
  formLabelSx?: SxProps<Theme>;
  hasErrorOverride?: boolean;
  isBold?: boolean;
  isRequired?: boolean;
  label: ReactNode | undefined;
  wrapperSx: SxProps<Theme>;
}) => {
  return (
    <FormWatcher watchPath={elementName}>
      {({ watchedValidation }) => {
        return (
          <WithLabel
            formLabelSx={formLabelSx}
            hasError={
              typeof hasErrorOverride === "boolean"
                ? hasErrorOverride
                : !isValid(watchedValidation)
            }
            isBold={isBold}
            isRequired={isRequired}
            label={label}
            wrapperSx={wrapperSx}
          >
            {children}
          </WithLabel>
        );
      }}
    </FormWatcher>
  );
};

export const DatePickerWithLabel = (
  props: ConnectedDateSelectorProps & {
    elementName: string;
    isBold?: boolean;
    wrapperSx?: SxProps;
  },
) => {
  const { isBold, label, ...restProps } = props;
  return (
    <LabelWrapper
      elementName={restProps.elementName}
      isBold={isBold}
      isRequired={restProps.isRequired}
      label={label}
      wrapperSx={{
        minWidth: SMALL_INPUT_MIN_WIDTH,
        width: SMALL_INPUT_WIDTH,
        ...(restProps?.wrapperSx ?? {}),
      }}
    >
      <DatePickerInputField
        {...restProps}
        formHelperTextProps={{ style: { margin: margin(1, 0, 0) } }}
        hasCustomValidation
        inputSx={{
          "& .MuiInputBase-root": {
            background: WHITE,
          },
          minWidth: dp(165),
          width: "100%",
          ...(restProps?.inputSx ?? {}),
        }}
        textInputVariant="outlined"
      />
    </LabelWrapper>
  );
};

const CharacterLimit = ({
  characterLimit,
  characterLimitId,
  elementName,
}: {
  characterLimit?: number;
  characterLimitId: string;
  elementName: string;
}) => {
  const translations = useTranslations();

  if (!characterLimit) return null;

  return (
    <FormWatcher watchPath={elementName}>
      {({ watchedValidation, watchedValue }) => {
        if (watchedValidation !== true) return null;

        const characterLimitCounter = getCharacterCounter({
          characterLimit: characterLimit,
          translations,
          value: watchedValue,
        });

        return (
          <FormHelperText id={characterLimitId}>
            {characterLimitCounter}
          </FormHelperText>
        );
      }}
    </FormWatcher>
  );
};

export const TextInputWithLabel = ({
  characterLimit,
  elementName,
  inputProps = {},
  isBold,
  isLarge,
  label,
  labelWrapperSx,
  ...restProps
}: ConnectedTextInputProps & {
  characterLimit?: number;
  elementName: string;
  isBold?: boolean;
  isLarge?: boolean;
  labelWrapperSx?: SxProps<Theme>;
}) => {
  const characterLimitId = `${elementName}-character-limit`;
  return (
    <LabelWrapper
      elementName={elementName}
      isBold={isBold}
      isRequired={restProps.required}
      label={label}
      wrapperSx={{
        minWidth: isLarge ? LARGE_INPUT_MIN_WIDTH : SMALL_INPUT_MIN_WIDTH,
        whiteSpace: "nowrap",
        width: isLarge ? LARGE_INPUT_WIDTH : SMALL_INPUT_WIDTH,
        ...labelWrapperSx,
      }}
    >
      <ConnectedTextInputField
        ariaDescribedBy={characterLimit ? characterLimitId : undefined}
        elementName={elementName}
        inputProps={{
          ...inputProps,
          maxLength: characterLimit,
        }}
        label={null}
        style={{ margin: "0", width: "100%" }}
        variant="outlined"
        {...restProps}
        hasCustomValidation
      />
      <CharacterLimit
        characterLimit={characterLimit}
        characterLimitId={characterLimitId}
        elementName={elementName}
      />
    </LabelWrapper>
  );
};

export const TextAreaWithLabel = (
  props: TextAreaProps &
    FormElementProps & {
      elementName: string;
      formLabelSx?: SxProps<Theme>;
      isBold?: boolean;
      wrapperSx?: SxProps<Theme>;
    },
) => {
  const { formLabelSx, isBold, label, wrapperSx, ...restProps } = props;
  return (
    <LabelWrapper
      elementName={restProps.elementName}
      formLabelSx={formLabelSx}
      hasErrorOverride={props.hideError ? false : undefined}
      isBold={isBold}
      isRequired={props.required}
      label={label}
      wrapperSx={{
        width: "100%",
        ...wrapperSx,
      }}
    >
      <ConnectedTextarea label={null} {...restProps} />
    </LabelWrapper>
  );
};

export const SelectWithLabel = (
  props: ConnectedSelectInputProps & {
    elementName: string;
    inputSx?: SxProps<Theme>;
    isBold?: boolean;
    isLarge?: boolean;
    wrapperSx: SxProps<Theme>;
  },
) => {
  const { inputSx, isBold, label, wrapperSx, ...restProps } = props;
  return (
    <LabelWrapper
      elementName={restProps.elementName}
      isBold={isBold}
      isRequired={restProps.required}
      label={label}
      wrapperSx={wrapperSx}
    >
      <ConnectedSelectInput
        label={null}
        {...restProps}
        inputSx={{
          background: WHITE,
          minWidth: props.isLarge
            ? LARGE_INPUT_MIN_WIDTH
            : SMALL_INPUT_MIN_WIDTH,
          width: props.isLarge ? LARGE_INPUT_WIDTH : SMALL_INPUT_WIDTH,
          ...inputSx,
        }}
        size="small"
        variant="outlined"
      />
    </LabelWrapper>
  );
};

export const ActivableToggle = ({
  componentIfFalse,
  componentIfTrue,
  elementName,
  label,
  sideMutation,
  wrapperStyle,
}: {
  componentIfFalse?: React.ReactNode;
  componentIfTrue?: React.ReactNode;
  elementName: string;
  label: string;
  sideMutation?: (newValue: boolean, mutateElement: MutateElementFn) => void;
  wrapperStyle?: CSSProperties;
}) => {
  return (
    <FormWatcher watchPath={elementName}>
      {({ watchedValue }) => {
        return (
          <VerticalLayout gap={sizing(2)} overflow="visible">
            <ToggleInputField
              elementName={elementName}
              label={label}
              shouldHideIndent
              sideMutation={sideMutation}
              wrapperStyle={{
                overflow: "visible",
                padding: padding(0),
                width: "100%",
                ...wrapperStyle,
              }}
            />
            {watchedValue ? componentIfTrue : componentIfFalse}
          </VerticalLayout>
        );
      }}
    </FormWatcher>
  );
};

export const FootnoteDescription = ({ footnote }: { footnote: Footnote }) => {
  const translations = useTranslations();

  return (
    <Caption aria-hidden margin={margin(0, 0, 1)}>
      <sup style={{ paddingRight: dp(2) }}>{footnote.superText}</sup>
      {footnote.description(translations)}
    </Caption>
  );
};

export const FootnoteLabel = ({ footnote }: { footnote: Footnote }) => {
  const translations = useTranslations();

  return (
    <>
      <span style={visuallyHidden}>
        {getFootnoteAriaLabel(footnote, translations)}
      </span>
      <span aria-hidden style={{ position: "relative" }} tabIndex={-1}>
        {footnote.label(translations)}
        <sup
          aria-hidden
          style={{ paddingLeft: dp(2), position: "absolute", top: -8 }}
          tabIndex={-1}
        >
          {footnote.superText}
        </sup>
      </span>
    </>
  );
};

export const YesNoRadioWithLabel = ({
  elementName,
  formLabelSx,
  isRequired,
  label,
  shouldStartWithNo,
  sideMutation,
  subForms = {},
  withFlatModel,
}: {
  elementName: string;
  formLabelSx?: CSSProperties;
  isRequired?: boolean;
  label: string;
  shouldStartWithNo?: boolean;
  sideMutation?: (
    newValue: any,
    mutateElement: (value: any, elementName: string) => void,
  ) => void;
  subForms?: { [key: string]: React.ReactNode };
  withFlatModel?: boolean;
}) => {
  const translations = useTranslations();

  const defaultYesNoOptions: RadioOptionV2<PredicamentState>[] = [
    {
      label: translations.actions.yes,
      subForm: subForms[PREDICAMENT_STATE_YES],
      value: PREDICAMENT_STATE_YES,
    },
    {
      label: translations.actions.no,
      subForm: subForms[PREDICAMENT_STATE_NO],
      value: PREDICAMENT_STATE_NO,
    },
  ];

  return (
    <RadioGroupV2
      elementName={elementName}
      formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
      formLabelSx={{
        color: important(CUSTOM_BLACK),
        fontSize: FONT_SIZE_14,
        padding: padding(1, 0),
        ...formLabelSx,
      }}
      isRequired={isRequired}
      label={label}
      options={
        shouldStartWithNo ? defaultYesNoOptions.reverse() : defaultYesNoOptions
      }
      radioSx={RADIO_WHITE_BACKGROUND}
      sideMutation={sideMutation}
      withFlatModel={withFlatModel}
    />
  );
};

export const DownloadSectionWrapper = ({
  body,
  lastUpdated,
  onClickPrint,
  onClickSendToHis,
  pdfName,
  progressOpenPdf,
  progressSendToHis,
  title,
}: {
  body: string;
  lastUpdated: number;
  onClickPrint: () => void;
  onClickSendToHis: () => void;
  pdfName: string;
  progressOpenPdf: QueryProgressActive;
  progressSendToHis: QueryProgressActive;
  title: string;
}) => {
  const translations = useTranslations();
  const { currentLocale: locale } = useDateLocale();
  const { hasTwoWaySync } = useHL7ImportStatusContext();

  return (
    <VerticalLayout
      data-testid="pdf-download-section"
      margin={margin(0, 0, 3)}
      padding={padding(2, 3, 1)}
      style={{
        border: border({ color: PRIMARY_DARK_COLOR, width: 2 }),
        borderRadius: sizing(1),
        boxSizing: "border-box",
      }}
      width="100%"
    >
      <Title as="h2" margin={margin(0, 0, 1.5)}>
        {title}
      </Title>
      <Body
        as="p"
        fontSize={FONT_SIZE_14}
        fontWeight={FONT_WEIGHT_REGULAR}
        margin="0"
        maxWidth="100%"
      >
        {body}
      </Body>
      <HorizontalLayout aligned style={{ columnGap: sizing(3) }}>
        <HorizontalLayout aligned>
          <Body
            as="p"
            fontSize={FONT_SIZE_14}
            fontWeight={FONT_WEIGHT_BOLD}
            margin="0"
          >
            {pdfName}
          </Body>
          <LockIcon
            size={16}
            style={{ color: GREY_600, margin: margin(0, 0, 0, 1) }}
          />
        </HorizontalLayout>
        <Body
          as="p"
          color={GREY_600}
          fontSize={FONT_SIZE_14}
          fontWeight={FONT_WEIGHT_MEDIUM}
          margin="0"
        >
          {translations.patientForms.transitionalCareForm.downloadSection.savedAt(
            {
              savedAt: formatDateTime(lastUpdated, lastUpdated, locale),
            },
          )}
        </Body>
        <RSButton
          color="primary"
          id="download"
          LeftIcon={() => <PrinterIcon size={16} />}
          loading={progressOpenPdf}
          style={{ width: "inherit" }}
          variant="text"
          onClick={onClickPrint}
        >
          {
            translations.patientForms.transitionalCareForm.downloadSection
              .printButton
          }
        </RSButton>
        {hasTwoWaySync ? (
          <RSButton
            color="primary"
            id="send_to_his"
            LeftIcon={() => <FolderInputIcon size={FONT_SIZE_16} />}
            loading={progressSendToHis}
            variant="text"
            onClick={onClickSendToHis}
          >
            {
              translations.patientForms.transitionalCareForm.downloadSection
                .hisButton
            }
          </RSButton>
        ) : null}
      </HorizontalLayout>
    </VerticalLayout>
  );
};

export const PatientFormsSectionHeader = ({
  children,
}: {
  children: string;
}) => {
  return (
    <Subheading as="h4" margin={margin(0)} style={{ fontSize: FONT_SIZE_24 }}>
      {children}
    </Subheading>
  );
};

export const PatientFormSubheading = ({
  children,
  style,
  withBottomPadding,
}: {
  children: string;
  style?: CSSProperties;
  withBottomPadding?: boolean;
}) => (
  <Subheading
    as="h5"
    style={{
      fontSize: FONT_SIZE_18,
      fontWeight: FONT_WEIGHT_BOLD,
      margin: margin(0, 0, 0.5, 0.5),
      padding: padding(GAP, 0, withBottomPadding ? 1 : 0),
      ...style,
    }}
  >
    {children}
  </Subheading>
);

function OpsFields({
  onChange,
  opsIndex,
  prefix,
  validation,
  value,
}: {
  onChange: (newValue: string[], statePath?: string) => void;
  opsIndex: number;
  prefix: string;
} & Pick<FormElementProps, "value" | "validation">) {
  const translations = useTranslations();

  const { initialOps, maxFilledIndex } = useMemo(() => {
    const ops: string[] = Array.isArray(value)
      ? [...value, ...Array(NUMBER_OF_OPS_FIELDS - value.length).fill("")]
      : Array(NUMBER_OF_OPS_FIELDS).fill("");

    const lastFilledIndex = ops.reduce((max, v, i) => {
      return v != null && v !== "" ? i + 1 : max;
    }, 0);

    return {
      initialOps: ops,
      maxFilledIndex: lastFilledIndex,
    };
  }, [value]);

  const {
    canIncrement,
    count: visibleCount,
    increment,
  } = useVisibleCount({
    initialCount: Math.max(maxFilledIndex, 1),
    maxCount: NUMBER_OF_OPS_FIELDS,
  });

  const handleOpChange = (index: number, newValue: string) => {
    const updated = [...initialOps];
    updated[index] = newValue;
    onChange(updated);
  };

  const { baseValidation, perItemErrors } = parsePerItemValidation(validation);

  return (
    <>
      {initialOps.map((opValue, index) => {
        if (index >= visibleCount) return null;

        const labelIndex = index + 1;
        const itemError = perItemErrors[index];

        return (
          <LabelWrapper
            key={index}
            elementName={`${prefix}_ops_${labelIndex}`}
            isBold
            label={`${translations.patientForms.medicalForm.sectionTwo.surgeryOps} ${labelIndex}`}
            wrapperSx={{
              minWidth: LARGE_INPUT_MIN_WIDTH,
              whiteSpace: "nowrap",
              width: LARGE_INPUT_MIN_WIDTH,
            }}
          >
            <TextInputField
              elementName={`${prefix}_ops_${labelIndex}`}
              hasCustomValidation
              label={`${translations.patientForms.medicalForm.sectionTwo.surgeryOps} ${labelIndex}`}
              style={{ margin: margin(0) }}
              validation={itemError ? { infos: itemError } : baseValidation}
              value={opValue}
              variant="outlined"
              onChange={(v) => handleOpChange(index, v)}
            />
          </LabelWrapper>
        );
      })}
      <div className="mt-2 flex justify-between">
        <RSButton
          buttonWrapperStyle={{ width: LARGE_INPUT_MIN_WIDTH }}
          color="primary"
          disabled={!canIncrement}
          id={`operation-${opsIndex}-add-ops-code-field`}
          LeftIcon={PlusIcon}
          loading="na"
          style={{
            borderRadius: dp(8),
            margin: margin(0),
            width: LARGE_INPUT_MIN_WIDTH,
          }}
          trackEventProps={{
            name: TRACK_EVENTS.OPERATION_ADD_OPS_CODE_FIELD,
            visible_ops_code_fields: visibleCount,
          }}
          type="button"
          variant="outlined"
          onClick={increment}
        >
          {translations.patientForms.medicalForm.sectionTwo.addOpsCode}
        </RSButton>
      </div>
    </>
  );
}

export function OperationsSubForm({
  initialVisibleOpsFields,
}: {
  initialVisibleOpsFields: number;
}) {
  const translations = useTranslations();
  const {
    canIncrement: canIncrementOpsFields,
    count: visibleOpsFields,
    increment: incrementOpsCount,
  } = useVisibleCount({
    initialCount: initialVisibleOpsFields,
    maxCount: Object.values(OPERATION_INDEXES).length,
  });

  return (
    <div className="flex w-full max-w-lg flex-col space-y-3 pl-7">
      <div className="flex w-full flex-col">
        {Object.values(OPERATION_INDEXES).map((opsIndex) => {
          const prefix = `operation_${opsIndex}`;
          return (
            <div
              key={opsIndex}
              className={clsx("flex w-full gap-4", {
                hidden: opsIndex > visibleOpsFields,
                "pt-8": opsIndex > 1,
              })}
            >
              <div>
                <DatePickerWithLabel
                  elementName={`${prefix}_date`}
                  isBold
                  label={
                    translations.patientForms.medicalForm.sectionTwo.surgeryDate
                  }
                  withFlatModel
                />
              </div>
              <div className="flex w-full max-w-[316px] flex-col space-y-4">
                <FormElementRenderProps
                  elementName={`${prefix}_ops`}
                  withFlatModel
                >
                  {({ onChange, validation, value }) => (
                    <OpsFields
                      opsIndex={opsIndex}
                      prefix={prefix}
                      validation={validation}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                </FormElementRenderProps>
                <YesNoRadioWithLabel
                  elementName={`${prefix}_wound_closed`}
                  label={translations.patientForms.medicalForm.sectionTwo.wound}
                  withFlatModel
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between">
        <RSButton
          buttonWrapperStyle={{ width: "100%" }}
          color="primary"
          disabled={!canIncrementOpsFields}
          id="add-operation-field"
          LeftIcon={PlusIcon}
          loading="na"
          style={{
            borderRadius: 8,
            margin: 0,
            width: "100%",
          }}
          trackEventProps={{
            name: TRACK_EVENTS.OPERATION_ADD_FIELD,
            visible_operation_fields: visibleOpsFields,
          }}
          type="button"
          variant="outlined"
          onClick={incrementOpsCount}
        >
          {translations.patientForms.medicalForm.sectionTwo.addAnotherOperation}
        </RSButton>
      </div>
    </div>
  );
}
